import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { Application } from 'merchant-api/types';
import { getApplicationOpts } from './useGetApplication';

const DEFAULT_POLL_MS = 3_000;

export interface WaitForAppStatusChange {
  applicationData: Application | null | undefined;
  /** The status the application had when the wait started (from a fresh read, never cache). */
  baselineStatus?: string;
  /** True while the polled status differs from the baseline. */
  changed: boolean;
}

/**
 * Poll an application and report when its status differs from the status it had when the
 * wait started. Callers decide what to do with the new state and stop the poll by passing
 * `undefined` as the applicationId once they are done.
 *
 * The baseline is only ever captured from a fetch issued AFTER this hook mounted: the query
 * cache can hold a status from before the caller's step began (e.g. pre-dating a patch or an
 * orchestration that progressed in the background), and diffing against that stale value
 * would report a "change" this wait never actually witnessed.
 *
 * The poll deliberately never throws - a single flaky refetch inside the window must not
 * crash the caller.
 */
export function useWaitForAppStatusChange(applicationId?: string, pollMs = DEFAULT_POLL_MS): WaitForAppStatusChange {
  const mountedAt = useRef(Date.now());
  const baseline = useRef<string | undefined>(undefined);

  const { data, dataUpdatedAt } = useQuery({
    ...getApplicationOpts(applicationId ?? ''),
    enabled: !!applicationId,
    throwOnError: false,
    retry: false,
    staleTime: 0,
    refetchInterval: pollMs,
  });

  if (baseline.current === undefined && data?.status && dataUpdatedAt >= mountedAt.current) {
    baseline.current = data.status;
  }

  return {
    applicationData: data,
    baselineStatus: baseline.current,
    changed: !!data?.status && !!baseline.current && data.status !== baseline.current,
  };
}

export default useWaitForAppStatusChange;
